/** Plate text normalization. One rule set for client and server so a verdict means the same thing everywhere. */
import { INVISIBLE_CHARS } from './sanitize';

const PLACEHOLDER = /^(n\/?a|na|none|null|nil|unknown|unk|tbd|tba|see plate|[-*?_.#\s\u2010-\u2015\u2212]+)$/i;
/** A label printed before the value, only when followed by a separator (so "SN123" keeps its "SN"). */
const LABEL = /^(S\s*\/\s*N|SN|SER(?:IAL)?(?:\s*(?:NO|NUMBER|#))?|NO|UI|MODEL|MOD)[\s:#.\-]+/i;
const DECORATION_CHARS = '*·•';

/** Most OCR characters ever tokenized; longer text is cut before any work is done. */
export const MAX_OCR_CHARS = 20000;

/** Trim barcode asterisks / middle dots from both ends with two index scans (a regex here is quadratic). */
function stripDecoration(s: string): string {
  let start = 0;
  let end = s.length;
  while (start < end && DECORATION_CHARS.includes(s[start])) start++;
  while (end > start && DECORATION_CHARS.includes(s[end - 1])) end--;
  return s.slice(start, end);
}

/**
 * Invisible characters (the sanitizer's set, sanitize.ts) must never change what a value is (RVW-A r3 L1): the verdict judges the
 * raw reader text and the panel and writer act on the sanitized one, so both must compare the same characters. Controls such as a
 * tab are NOT removed here: they stay separators.
 */
const HIDDEN = new RegExp(`[${INVISIBLE_CHARS}]`, 'g');

/** Placeholder, decoration and label removal; the text that is left is what a person actually wrote as the value. */
function stripWrappers(raw: string | null | undefined): string {
  if (raw == null) return '';
  // Hidden characters out, then NFKC, so a fullwidth placeholder or label (ＮＡ, ＳＮ：) is caught like its ASCII form.
  let s = String(raw).replace(HIDDEN, '').normalize('NFKC').trim();
  if (!s || PLACEHOLDER.test(s)) return '';
  s = stripDecoration(s).trim();
  for (let i = 0; i < 3; i++) s = s.replace(LABEL, '');
  return s;
}

const toAlnum = (s: string): string => s.normalize('NFKC').toUpperCase().replace(/[^A-Z0-9]/g, '');

export function normalizePlate(raw: string | null | undefined): string {
  return toAlnum(stripWrappers(raw));
}

/**
 * The value as normalizePlate sees it BEFORE separators are dropped (NFKC, upper-case, wrappers removed). Its A-Z0-9 characters, in
 * order, are exactly normalizePlate(raw); the rest are the separators the containment test reads token edges from.
 */
export function plateText(raw: string | null | undefined): string {
  return stripWrappers(raw).normalize('NFKC').toUpperCase();
}

/**
 * Is a stored/entered value empty, usable, or present-but-unreadable by us?
 * 'unusable' = something is there (e.g. only non-Latin characters) but it normalizes to nothing: it is NOT blank,
 * so it must never be treated as an empty field a plate could "fill".
 */
export function classifyRecordValue(raw: string | null | undefined): 'blank' | 'usable' | 'unusable' {
  const stripped = stripWrappers(raw);
  if (!stripped) return 'blank';
  return toAlnum(stripped) ? 'usable' : 'unusable';
}

/**
 * Characters a camera or a person commonly confuses, each folded onto one class. FULL set (pinned by test):
 * O, D, Q -> 0 | I, L -> 1 | S -> 5 | B -> 8 | Z -> 2 | G -> 6.
 * O/0, I/1, S/5, B/8 and Z/2 are the classic confusions; D, Q, L and G are deliberately wider. A wider fold can only
 * turn a would-be 'conflict' into 'unsure' (the safe direction), never the other way, so the extras stay.
 */
export const LOOKALIKE_FOLDS: Readonly<Record<string, string>> = Object.freeze({
  O: '0', D: '0', Q: '0', I: '1', L: '1', S: '5', B: '8', Z: '2', G: '6',
});

/** Map lookalike characters onto one class. Input must already be normalized. */
export function foldLookalikes(norm: string): string {
  return norm.replace(/[ODQILSBZG]/g, (c) => LOOKALIKE_FOLDS[c]);
}

/** Drop zeros between an optional alpha prefix and the first non-zero digit. Input must already be normalized. */
export function foldLeadingZeros(norm: string): string {
  const folded = norm.replace(/^([A-Z]*)0+(?=\d)/, '$1');
  return folded === '' ? norm : folded;
}

export function normalizeOcr(text: string | null | undefined): string {
  return text ? toAlnum(text) : '';
}

/** OCR text as A-Z0-9 tokens (NFKC, upper-case, split on everything else). Cut at MAX_OCR_CHARS first. */
export function ocrTokens(text: string | null | undefined): string[] {
  if (!text) return [];
  return text.slice(0, MAX_OCR_CHARS).normalize('NFKC').toUpperCase().split(/[^A-Z0-9]+/).filter(Boolean);
}
